/**
 * User Utilities
 * 
 * Static utility methods for user-related operations.
 * Matches Flutter TextUtils class functionality from lib/presentation/utils/text_utils.dart
 * 
 * All methods are static - no instantiation needed.
 */

import { env } from '@/config/env';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { UserNameInfoDto } from '@/data/dtos/user/userNameInfoDto';

/**
 * UserUtils - User-related utility functions
 * 
 * Provides methods for generating user profile image URLs,
 * identity verification badge URLs, and other user-related utilities.
 */
export class UserUtils {
  /**
   * Generate user profile image URL
   * 
   * @param firebaseId - User's Firebase ID
   * @returns Full URL to user's profile image      
   */
  static generateUserProfileImageUrl(firebaseId: string): string {
    return `${env.USER_PROFILES_BUCKET_PREFIX}${firebaseId}/${firebaseId}`;
  }

  /**
   * Generate user profile image URL with encoded path
   * 
   * @param firebaseId - User's Firebase ID
   * @returns URL-encoded profile image path
   * 
   */
  static generateUserProfileEncodedImageUrl(firebaseId: string): string {
    return `${env.USER_PROFILES_BUCKET_PREFIX}${firebaseId}%2F${firebaseId}`;
  }

  /**
   * Generate short user profile image URL (bucket name only)
   * 
   * @param firebaseId - User's Firebase ID
   * @returns Short profile image path with bucket name   
   */
  static generateUserProfileShortImageUrl(firebaseId: string): string {
    return `${env.USER_PROFILES_BUCKET_NAME}%2F${firebaseId}`;
  }

  /**
   * Generate user identity verification badge URL (blue checkmark)
   * 
   * @param firebaseId - User's Firebase ID
   * @returns URL to verification badge image
   * 
   */
  static generateUserIdentityVerificationBlueBadgeUrl(firebaseId: string): string {
    return `${env.IDENTITY_BUCKET}${firebaseId}/verify.png`;
  }

  /**
   * Generate user identity verification badge URL with encoded path
   * 
   * @param firebaseId - User's Firebase ID
   * @returns URL-encoded verification badge path
   * 
   */
  static generateUserIdentityEncodedVerificationBlueBadgeUrl(firebaseId: string): string {
    return `${env.IDENTITY_BUCKET}${firebaseId}%2Fverify.png`;
  }

  /**
   * Get user display name
   * Shows "You" for current user, otherwise returns the provided username
   * 
   * @param userId - User's ID
   * @param userName - User's display name
   * @param currentUserId - Current authenticated user's ID
   * @returns Display name string
   * 
   * @example
   * const displayName = UserUtils.getDisplayName('user1', 'John Doe', 'user1');
   * // Returns: "You"
   * 
   * const displayName = UserUtils.getDisplayName('user2', 'Jane Smith', 'user1');
   * // Returns: "Jane Smith"
   */
  static getDisplayName(
    userId: string,
    userName: string,
    currentUserId?: string
  ): string {
    return currentUserId === userId ? 'You' : userName;
  }

  /**
   * Generate user name info from a display name string.
   * Matches Flutter's TextUtils.generateUserNameInfo from text_utils.dart.
   *
   * @param name - Full display name (e.g. "John Doe")
   * @returns UserNameInfoDto with firstName, lastName, and joined lowercase search fields
   */
  static generateUserNameInfo(name: string): UserNameInfoDto {
    const trimmed = name.trim();
    const nameParts = trimmed.split(' ');
    const firstName = nameParts[0] ?? '';
    const lastName = nameParts.slice(1).join(' ');

    return {
      name: trimmed,
      firstName,
      lastName,
      firstLastNameJoinedLowerCase: `${firstName} ${lastName}`.trim().toLowerCase(),
      lastFirstNameJoinedLowerCase: `${lastName} ${firstName}`.trim().toLowerCase(),
    };
  }

  /**
   * Check whether a profile image string already lives in the user profiles bucket.
   *
   * Port of the string-level test Flutter applies in
   * `UsersRepository.saveUserDetails` (lib/data/repository/users_repository.dart:65-67):
   * `!profileImageUrl.contains(Env.userProfilesBucketName)` → "the user picked a new
   * image". This is the single home of that substring rule; `isProfileImageUploaded`
   * delegates to it, mirroring Flutter's two call sites of the same test
   * (`:65-67` on a raw string, `user.dart:108-110` on the model).
   *
   * Two properties are deliberate — do not "improve" them:
   * - It tests the **bucket name** (`expause-profiles`) substring, never the prefix
   *   URL (`env.USER_PROFILES_BUCKET_PREFIX`). Flutter uses `Env.userProfilesBucketName`.
   * - It is a **loose `includes`** on purpose: both the short persisted form
   *   `expause-profiles%2F{uid}` (`generateUserProfileShortImageUrl`, :48-50) and the
   *   full read URL `https://storage.googleapis.com/expause-profiles/profile%2F{uid}/{uid}`
   *   (`generateUserProfileImageUrl`, :27-29) must satisfy it, because Flutter feeds
   *   both shapes through this one test.
   *
   * An empty string returns `false` — i.e. callers treat it as "changed", matching
   * Flutter, where `'' != null` is likewise true.
   *
   * @param profileImage - Raw profile image URL or path
   * @returns true if the string already points at the profiles bucket
   */
  static isProfileImageInProfilesBucket(profileImage: string): boolean {
    return profileImage.includes(env.USER_PROFILES_BUCKET_NAME);
  }

  /**
   * Check if profile image has been uploaded to user profiles bucket
   * Matches Flutter's profileImageUploaded getter (lib/data/models/user.dart:108-110),
   * which returns false for a null image and otherwise applies the same
   * bucket-name substring test as `isProfileImageInProfilesBucket`.
   *
   * @param user - User entity to check
   * @returns true if profile image is uploaded to profiles bucket
   */
  static isProfileImageUploaded(user: UserEntity): boolean {
    return !!user.profileImage && UserUtils.isProfileImageInProfilesBucket(user.profileImage);
  }
}
